import React, { useState, useEffect } from "react";
import styles from "./styles/BusinessForm.module.css";

import BusinessAudience from "./slides/BusinessAudience";
import BusinessInfo from "./slides/BusinessInfo";
import BusinessUSP from "./slides/BusinessUSP";
import IndustryInfo from "./slides/IndustryInfo";
import BrandAssists from "./slides/BrandAssets";
import BrandSocials from "./slides/BrandSocials";
import { useAtom } from "jotai";
import { orgOBAtom } from "../onboarding/BrandOrganizationSetup";
import { useUpdateItemMutation } from "../_actions/app/items";

import { Button, message, Steps } from "antd";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";

import { useBusiness } from "../_actions/finance";
import { useCurrentUser } from "../actions/auth";
import { useCreateItemMutation } from "../_actions/app/items"; //import createItem instead of update
import { nanoid } from "nanoid"; ///unique id will be required
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { snakeCase } from "lodash";

export default function BusinessForm({ onDone }) {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [answers, setAnswers] = useState({});
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [nextButtonProps, setNextButtonProps] = useState({});
    const [orgOB, setOrgOB] = useAtom(orgOBAtom);
    const history = useHistory();

    useEffect(() => {
        if (currentSlide !== 0) {
            setNextButtonProps({});
        }
    }, [currentSlide]);

    const slides = [
        { component: BusinessInfo, name: "Information" },
        { component: IndustryInfo, name: "Industry" },
        { component: BusinessUSP, name: "USP" },
        { component: BusinessAudience, name: "Audience" },
        { component: BrandAssists, name: "BrandAssist" },
        { component: BrandSocials, name: "Communication" },
    ];

    const { data: dataMe } = useCurrentUser();

    const { mutate: Businessdata } = useBusiness(
        "business/process_description",
        (response) => {
            const apiData = response.data;

            setAnswers((prev) => ({
                ...prev,
                name: apiData.name,
                businessDescription: apiData.description,
                businessModel: apiData.business_model,
                industry: apiData.industry,
                businessUSP: apiData.USP,
                targetInterests: apiData.brand_vibes,
                targetAudience: apiData.target_audience,
            }));

            setCurrentSlide((prev) => prev + 1);
            setLoading(false);
        },
        () => {
            setLoading(false);
        },
        (error) => {
            message.error(
                `Failed to process business information: ${
                    error?.response?.data?.message || error.message
                }`
            );
            setLoading(false);
        }
    );

    const mutation_create = useCreateItemMutation();

    const handleAnswerChange = (key, value) => {
        setAnswers((prev) => ({
            ...prev,
            [key]: value,
        }));
    };
    const updateUser = useUpdateItemMutation();

    const nextSlide = (status, apiData) => {
        if (status === "publish") {
            if (currentSlide === 0) {
                if (apiData || answers.apiData) {
                    const dataToUse = apiData || answers.apiData;

                    setAnswers((prev) => ({
                        ...prev,
                        name: dataToUse.name,
                        businessModel: dataToUse.businessModel,
                        industry: dataToUse.industry,
                        businessUSP: dataToUse.businessUSP,
                        targetInterests: dataToUse.targetInterests,
                        targetAudience: dataToUse.targetAudience,
                    }));

                    setCurrentSlide((prev) => prev + 1);
                    return;
                }

                if (!answers.businessDescription?.trim()) {
                    if (
                        nextButtonProps.text === "Import from URL" ||
                        nextButtonProps.loading
                    ) {
                        return; // do NOT show warning
                    }

                    message.warning(
                        "Please enter business information, import from URL, or upload a document."
                    );
                    return;
                }

                if (!dataMe?.selected_org?.id) {
                    message.error("Organization not found. Please try again.");
                    return;
                }

                setLoading(true);
                Businessdata({
                    content: answers.businessDescription,
                    selected_org: dataMe.selected_org.id,
                });
                return;
            }

            if (currentSlide < slides.length - 1) {
                setCurrentSlide((prev) => prev + 1);
            }
        } else {
            // Skip
            if (currentSlide < slides.length - 1) {
                setCurrentSlide((prev) => prev + 1);
            }
        }
    };

    const prevSlide = () => {
        if (currentSlide > 0) {
            setCurrentSlide((prev) => prev - 1);
        }
    };

    const handleSubmit = () => {
        setSubmitting(true);

        const businessProfileData = {
            name: answers.name,
            description: answers.businessDescription,
            business_model: answers.businessModel,
            industry: answers.industry,
            usp: answers.businessUSP,
            target_audience: answers.targetAudience,
            brand_vibes: answers.targetInterests,
            logo: answers.mainLogo,
            logo_square: answers.squareLogo,
            logo_dark: answers.darkLogo,
            primary_phone: answers.phone,
            primary_email: answers.email,
            address: answers.address,
            website: answers.website,
            brand_colors: answers.brandColors,
            linkedin_handle: answers.socialHandles?.linkedin,
            instagram_handle: answers.socialHandles?.instagram,
            facebook_handle: answers.socialHandles?.facebook,
            youtube_handle: answers.socialHandles?.youtube,
            pinterest_handle: answers.socialHandles?.pinterest,
            owner: dataMe?.id || dataMe?.user?.id,
            is_personal: false,
        };

        const organizationName = answers.name || "New Organization";
        const orgId = `${snakeCase(organizationName)}_${nanoid(5)}`;

        mutation_create.mutate(
            {
                collection: "qdb_organization",
                data: {
                    id: orgId,
                    ...businessProfileData,
                },
            },
            {
                onSuccess: () => {
                    updateUser.mutate(
                        {
                            collection: "directus_users",
                            id: dataMe.id,
                            data: { selected_org: orgId },
                        },
                        {
                            onSuccess: () => {
                                setOrgOB((prev) => ({
                                    ...prev,
                                    selected_org: {
                                        id: orgId,
                                        name: answers.name,
                                    },
                                }));

                                setSubmitting(false);

                                onDone?.();

                                setTimeout(() => window.location.reload(), 50);
                            },

                            onError: () => {
                                message.error(
                                    "Organization saved, but failed to switch active org."
                                );
                                setSubmitting(false);
                            },
                        }
                    );
                },

                onError: () => {
                    message.error("Failed to save business info. Try again.");
                    setSubmitting(false);
                },
            }
        );
    };

    const CurrentSlideComponent = slides[currentSlide].component;

    return (
        <div className={styles.contentWrapper}>
            {currentSlide > 0 && (
                <div className={styles.stepsContainer}>
                    <Steps
                        current={currentSlide - 1}
                        size="small"
                        responsive={false}
                        direction="horizontal"
                        items={slides.slice(1).map((slide, index) => ({
                            status:
                                index < currentSlide - 1
                                    ? "finish"
                                    : index === currentSlide - 1
                                    ? "process"
                                    : "wait",
                        }))}
                    />
                </div>
            )}

            <div className={styles.slideContainer}>
                <CurrentSlideComponent
                    dataMe={dataMe}
                    answers={answers}
                    onAnswerChange={handleAnswerChange}
                    onNextSlide={(apiData) => nextSlide("publish", apiData)}
                    setNextButtonProps={setNextButtonProps} // NEW functionality
                />
            </div>

            <div className={styles.navigation}>
                <Button onClick={prevSlide} disabled={currentSlide === 0}>
                    <ChevronLeftIcon size={20} /> Previous
                </Button>

                <div style={{ display: "flex", gap: "10px" }}>
                    {currentSlide < slides.length - 1 && (
                        <Button onClick={() => nextSlide("draft")}>Skip</Button>
                    )}

                    {currentSlide === slides.length - 1 ? (
                        <Button
                            type="primary"
                            onClick={handleSubmit}
                            loading={submitting}
                        >
                            Submit <ChevronRightIcon size={20} />
                        </Button>
                    ) : (
                        <Button
                            type="primary"
                            onClick={() => {
                                if (nextButtonProps.onClick) {
                                    nextButtonProps.onClick();
                                } else {
                                    nextSlide("publish");
                                }
                            }}
                            loading={
                                nextButtonProps.loading ||
                                (currentSlide === 0 && loading)
                            }
                            disabled={
                                nextButtonProps.loading ||
                                (currentSlide === 0 && loading)
                            }
                        >
                            {nextButtonProps.text || "Next"}
                            <ChevronRightIcon size={20} />
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}
